import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "./ui/button";
import { Cookie, Settings, X } from "lucide-react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import { Switch } from "./ui/switch";
import { Label } from "./ui/label";
import { api } from "../lib/api";

const STORAGE_KEY = "splink-cookie-consent";

const DEFAULT_PREFS = {
    essential: true,   // always on
    analytics: false,
    functional: false,
    security: true,
    thirdParty: false,
};

const CATEGORIES = [
    { id: "essential",  title: "Essential",              desc: "Required for login, session management, security, and core site functionality. Cannot be disabled.", locked: true },
    { id: "analytics",  title: "Performance & Analytics", desc: "Help us understand how visitors interact with the site (page views, browser, device)." },
    { id: "functional", title: "Functional",             desc: "Remember preferences like language, region, and other settings." },
    { id: "security",   title: "Security",               desc: "Detect suspicious activity and support fraud prevention." },
    { id: "thirdParty", title: "Third-Party",            desc: "Trusted third-party providers used for analytics, payments, or embedded services." },
];

function readConsent() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch { return null; }
}

function writeConsent(prefs) {
    const record = {
        prefs: { ...DEFAULT_PREFS, ...prefs, essential: true },
        decision: prefs.analytics || prefs.functional || prefs.thirdParty ? "accepted" : "rejected",
        set_at: new Date().toISOString(),
        version: "v1.0",
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    // Fire-and-forget: log the decision server-side for GDPR audit trail.
    api.post("/consent-log", {
        prefs: record.prefs,
        decision: record.decision,
        version: record.version,
    }).catch(() => {});
    return record;
}

export function CookieConsent() {
    const location = useLocation();
    const [decided, setDecided] = useState(true); // start hidden until we've checked storage
    const [manageOpen, setManageOpen] = useState(false);
    const [prefs, setPrefs] = useState(DEFAULT_PREFS);

    // Never render the banner on auth pages — it would overlap the submit CTA
    // and pressure users into a consent decision they can revisit any time later.
    const isAuthPage = /^\/(login|register|auth\/callback)/.test(location.pathname);

    useEffect(() => {
        const existing = readConsent();
        if (!existing) {
            setDecided(false);
        } else {
            setPrefs(existing.prefs || DEFAULT_PREFS);
        }
    }, []);

    const acceptAll = () => {
        writeConsent({ analytics: true, functional: true, security: true, thirdParty: true });
        setDecided(true);
    };
    const rejectAll = () => {
        writeConsent({ analytics: false, functional: false, security: true, thirdParty: false });
        setDecided(true);
    };
    const openManage = () => {
        const existing = readConsent();
        setPrefs(existing?.prefs || DEFAULT_PREFS);
        setManageOpen(true);
    };
    const savePrefs = () => {
        writeConsent(prefs);
        setManageOpen(false);
        setDecided(true);
    };

    return (
        <>
            {!decided && !isAuthPage && (
                <div
                    role="dialog"
                    aria-label="Cookie consent"
                    className="fixed bottom-4 left-1/2 z-50 w-[calc(100%-2rem)] max-w-2xl -translate-x-1/2 border-2 border-border bg-card p-5 shadow-2xl sm:bottom-6"
                    data-testid="cookie-consent-banner"
                >
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[hsl(27,96%,55%)]/15 text-[hsl(27,96%,55%)]">
                            <Cookie className="h-5 w-5" strokeWidth={1.5} />
                        </div>
                        <div className="flex-1">
                            <div className="font-display text-base font-semibold">
                                We use cookies to power SpLink.pro
                            </div>
                            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                                Essential cookies keep you signed in and secure. Optional cookies help us
                                improve analytics and personalization. Read our{" "}
                                <Link to="/cookies" className="font-semibold text-primary hover:underline" data-testid="consent-cookies-link">Cookie Policy</Link>
                                {" "}or{" "}
                                <Link to="/privacy" className="font-semibold text-primary hover:underline" data-testid="consent-privacy-link">Privacy Policy</Link>.
                            </p>
                        </div>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                        <Button size="sm" variant="outline" onClick={rejectAll} data-testid="cookie-reject-btn">
                            Reject non-essential
                        </Button>
                        <Button size="sm" variant="outline" onClick={openManage} data-testid="cookie-manage-btn">
                            <Settings className="mr-2 h-3.5 w-3.5" /> Manage
                        </Button>
                        <Button size="sm" className="bg-primary hover:bg-primary/90" onClick={acceptAll} data-testid="cookie-accept-btn">
                            Accept all
                        </Button>
                    </div>
                </div>
            )}

            <Dialog open={manageOpen} onOpenChange={setManageOpen}>
                <DialogContent data-testid="cookie-manage-dialog">
                    <DialogHeader>
                        <DialogTitle>Cookie preferences</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        {CATEGORIES.map((c) => (
                            <div key={c.id} className="flex items-start justify-between gap-4 border border-border bg-card p-4" data-testid={`consent-row-${c.id}`}>
                                <div className="flex-1">
                                    <Label className="font-display text-sm font-semibold">{c.title}
                                        {c.locked && <span className="ml-2 inline-flex items-center border border-[hsl(217,91%,45%)]/40 bg-[hsl(217,91%,45%)]/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest text-[hsl(217,91%,45%)]">Required</span>}
                                    </Label>
                                    <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{c.desc}</p>
                                </div>
                                <Switch
                                    checked={prefs[c.id]}
                                    onCheckedChange={(v) => setPrefs((p) => ({ ...p, [c.id]: !!v }))}
                                    disabled={c.locked}
                                    data-testid={`consent-toggle-${c.id}`}
                                />
                            </div>
                        ))}
                    </div>
                    <DialogFooter className="mt-4 flex-col gap-2 sm:flex-row">
                        <Button variant="outline" onClick={() => setManageOpen(false)} data-testid="consent-cancel-btn">
                            Cancel
                        </Button>
                        <Button onClick={savePrefs} className="bg-primary hover:bg-primary/90" data-testid="consent-save-btn">
                            Save preferences
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}

// Small "cookie preferences" button for footers so users can re-open the modal.
export function CookiePreferencesButton() {
    const [open, setOpen] = useState(false);
    const [prefs, setPrefs] = useState(DEFAULT_PREFS);

    const openManage = () => {
        const existing = readConsent();
        setPrefs(existing?.prefs || DEFAULT_PREFS);
        setOpen(true);
    };
    const save = () => {
        writeConsent(prefs);
        setOpen(false);
    };

    return (
        <>
            <button
                type="button"
                onClick={openManage}
                className="text-muted-foreground hover:text-foreground"
                data-testid="footer-cookie-prefs-btn"
            >
                Cookie preferences
            </button>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Cookie preferences</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        {CATEGORIES.map((c) => (
                            <div key={c.id} className="flex items-start justify-between gap-4 border border-border bg-card p-4">
                                <div className="flex-1">
                                    <Label className="font-display text-sm font-semibold">{c.title}
                                        {c.locked && <span className="ml-2 inline-flex items-center border border-[hsl(217,91%,45%)]/40 bg-[hsl(217,91%,45%)]/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest text-[hsl(217,91%,45%)]">Required</span>}
                                    </Label>
                                    <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{c.desc}</p>
                                </div>
                                <Switch
                                    checked={prefs[c.id]}
                                    onCheckedChange={(v) => setPrefs((p) => ({ ...p, [c.id]: !!v }))}
                                    disabled={c.locked}
                                />
                            </div>
                        ))}
                    </div>
                    <DialogFooter className="mt-4">
                        <Button onClick={save} className="bg-primary hover:bg-primary/90">Save</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
